import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, Eye, Pencil, Search, type LucideIcon } from 'lucide-react';
import { AppIcon } from '../../components/LucideIcon';
import '../../components/settings-dialog.css';
import '../workspace-target-rail.css';
import './drawing-search.css';
import { normalize, type SearchHit } from './searchIndex';

/** What a pick asks for: show selects and pans, edit opens the editor too, open goes to a drawing. */
export type SearchPickAction = 'show' | 'edit' | 'open';

const ACTION_ICON: Record<SearchPickAction, LucideIcon> = { show: Eye, edit: Pencil, open: ArrowRight };
const ACTION_VERB: Record<SearchPickAction, string> = { show: 'Show', edit: 'Edit', open: 'Open' };

/**
 * The row's actions, its default (Enter, a click) first. A drawing is not on this sheet,
 * so it only opens. Sheet text is no item with an editor, so it only shows.
 */
function actionsFor(hit: SearchHit): SearchPickAction[] {
  if (hit.kind === 'drawing') return ['open'];
  return hit.kind === 'click_target' ? ['show'] : ['show', 'edit'];
}

/** The empty query lists this many recent searches. */
const RECENT_SHOWN = 5;

type PaletteRow =
  | { kind: 'hit'; key: string; hit: SearchHit }
  | { kind: 'recent'; key: string; query: string };

/**
 * The drawing workspace's Ctrl+F / Cmd+F palette: one input over one flat list.
 *
 * The input keeps focus for the palette's whole life, which is what keeps the
 * canvas and workspace shortcuts off while it is open: each of them skips a key
 * typed into a field. A pick never takes focus, so rows are not tab stops.
 */
export default function DrawingSearchPalette({
  open, hits, query, onQueryChange, recent, onPick, onClose,
}: {
  open: boolean;
  hits: SearchHit[];
  query: string;
  onQueryChange: (query: string) => void;
  /** Earlier picks, newest first; shown while the query is empty. */
  recent: string[];
  onPick: (hit: SearchHit, action: SearchPickAction) => void;
  onClose: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const listboxId = useId();
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    openerRef.current = document.activeElement as HTMLElement | null;
    const surface = surfaceRef.current;
    inputRef.current?.focus();
    return () => {
      const opener = openerRef.current;
      // A Ctrl+F typed on the canvas leaves <body> as the opener; focusing it back does nothing.
      if (!opener || !opener.isConnected || opener === document.body) return;
      // What a pick opened (a confirm, an editor) may have taken focus already; it keeps it.
      const focused = document.activeElement;
      if (focused === null || focused === document.body || surface?.contains(focused)) opener.focus();
    };
  }, [open]);

  // A query of separators alone matches nothing, as searchHits reads it, so it is the empty query.
  const isQueryEmpty = normalize(query) === '';
  const rows: PaletteRow[] = isQueryEmpty
    ? recent.slice(0, RECENT_SHOWN).map((recentQuery) => ({ kind: 'recent', key: `recent:${recentQuery}`, query: recentQuery }))
    : hits.map((hit) => ({ kind: 'hit', key: hit.key, hit }));
  const rowKeys = rows.map((row) => row.key).join('|');
  useEffect(() => { setActiveIndex(0); }, [rowKeys]);
  // Only an arrow key scrolls the list: a row the pointer is over is already in view.
  const movedByKeyRef = useRef(false);
  useEffect(() => {
    if (!movedByKeyRef.current) return;
    movedByKeyRef.current = false;
    document.getElementById(`${listboxId}-option-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, listboxId]);

  if (!open) return null;

  const optionId = (index: number) => `${listboxId}-option-${index}`;
  const choose = (row: PaletteRow, { edit }: { edit: boolean }) => {
    if (row.kind === 'recent') {
      onQueryChange(row.query);
      return;
    }
    const actions = actionsFor(row.hit);
    onPick(row.hit, edit && actions.includes('edit') ? 'edit' : actions[0]);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    // The workspace's window listeners must not see a key meant for the palette.
    event.stopPropagation();
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (rows.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      movedByKeyRef.current = true;
      setActiveIndex((index) => (index + step + rows.length) % rows.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const row = rows[activeIndex];
      if (row) choose(row, { edit: event.shiftKey });
    } else if (event.key === 'Tab') {
      event.preventDefault();
    }
  };

  return createPortal(
    <div
      className="pnid-modal-scrim pnid-search-scrim"
      onMouseDown={(event) => {
        if (event.target !== event.currentTarget) return;
        // The browser would move focus to <body> after the close has given it back to the opener.
        event.preventDefault();
        onClose();
      }}
    >
      <div ref={surfaceRef} className="pnid-search" role="dialog" aria-modal="true" aria-label="Search this drawing" onKeyDown={onKeyDown}>
        <div className="pnid-search__field">
          <span className="pnid-search__control">
            <AppIcon icon={Search} size={18} className="pnid-search__icon" />
            <input
              ref={inputRef}
              className="pnid-search__input"
              type="text"
              role="combobox"
              aria-label="Search this drawing"
              aria-autocomplete="list"
              aria-expanded={rows.length > 0}
              aria-controls={listboxId}
              aria-activedescendant={rows.length > 0 ? optionId(activeIndex) : undefined}
              placeholder="Search this drawing"
              autoComplete="off"
              spellCheck={false}
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
            />
          </span>
        </div>
        <div className="pnid-search__body">
          {isQueryEmpty && rows.length > 0 && <div className="pnid-search__heading">Recent</div>}
          <ul className="pnid-search__list" role="listbox" id={listboxId} aria-label={isQueryEmpty ? 'Recent searches' : 'Matches'}>
            {rows.map((row, index) => (
              <li
                key={row.key}
                id={optionId(index)}
                role="option"
                aria-selected={index === activeIndex}
                className="pnid-search__option"
                // Keeps focus in the input, so the shortcut guards stay on through the click.
                onMouseDown={(event) => event.preventDefault()}
                onMouseMove={() => setActiveIndex(index)}
                onClick={() => choose(row, { edit: false })}
              >
                {row.kind === 'recent' ? (
                  <span className="pnid-search__primary">{row.query}</span>
                ) : (
                  <>
                    <span className="pnid-search__primary">{row.hit.primary}</span>
                    <span className="pnid-search__secondary">{row.hit.secondary}</span>
                    {/* On every row, not on hover, so a keyboard user sees what Shift+Enter does. */}
                    <span className="pnid-search__actions">
                      {actionsFor(row.hit).map((action) => (
                        <button
                          key={action}
                          type="button"
                          tabIndex={-1}
                          className="pnid-target-row-action"
                          aria-label={`${ACTION_VERB[action]} ${row.hit.primary}`}
                          title={ACTION_VERB[action]}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={(event) => {
                            event.stopPropagation();
                            onPick(row.hit, action);
                          }}
                        >
                          <AppIcon icon={ACTION_ICON[action]} size={14} />
                        </button>
                      ))}
                    </span>
                  </>
                )}
              </li>
            ))}
          </ul>
          {!isQueryEmpty && hits.length === 0 && (
            <p className="pnid-search__empty">Nothing on this drawing matches "{query.trim()}".</p>
          )}
        </div>
        <div className="pnid-search__foot" aria-hidden>
          <span><kbd>↑↓</kbd> move</span>
          <span><kbd>↵</kbd> show</span>
          <span><kbd>⇧↵</kbd> edit</span>
          <span><kbd>esc</kbd> close</span>
        </div>
      </div>
    </div>,
    document.body,
  );
}
